import { z } from "zod";

import { ArrayBinaryOperator, TernaryOperator } from "../../operators.ts";
import type { IResolvableStringArrayValue, IResolvableStringValue } from "../../values.ts";
import { ResolvableStringArrayValue, ResolvableStringValue } from "../../values.ts";
import type { IArrayBinaryCondition, IArrayTernaryCondition } from "./common.ts";
import { arrayBinaryCondition, arrayTernaryCondition } from "./common.ts";

// Zod value schemas
const StringArrayValue = z.union([z.array(z.string()), ResolvableStringArrayValue]);
const StringValue = z.union([z.string(), ResolvableStringValue]);

// Zod schemas with type discriminator
export const StringArrayBinaryCondition = arrayBinaryCondition(
  z.literal("stringArray"),
  ArrayBinaryOperator,
  StringArrayValue,
  StringValue,
);
export type StringArrayBinaryCondition = z.infer<typeof StringArrayBinaryCondition>;

export const StringArrayTernaryCondition = arrayTernaryCondition(
  z.literal("stringArray"),
  TernaryOperator,
  StringArrayValue,
  StringValue,
);
export type StringArrayTernaryCondition = z.infer<typeof StringArrayTernaryCondition>;

export const StringArrayCondition = z.union([StringArrayBinaryCondition, StringArrayTernaryCondition]);
export type StringArrayCondition = z.infer<typeof StringArrayCondition>;

// Interfaces for complex types (verified to match schemas)
export interface IStringArrayBinaryCondition extends IArrayBinaryCondition<
  string[] | IResolvableStringArrayValue,
  string | IResolvableStringValue
> {
  type: "stringArray";
}

export interface IStringArrayTernaryCondition extends IArrayTernaryCondition<
  string[] | IResolvableStringArrayValue,
  string | IResolvableStringValue
> {
  type: "stringArray";
}

// Type compatibility assertions - compile-time check that inferred types match interfaces
type _AssertBinaryCompatible = StringArrayBinaryCondition extends IStringArrayBinaryCondition ? true : never;
type _AssertTernaryCompatible = StringArrayTernaryCondition extends IStringArrayTernaryCondition ? true : never;
const _binaryCheck: _AssertBinaryCompatible = true;
const _ternaryCheck: _AssertTernaryCompatible = true;
void _binaryCheck;
void _ternaryCheck;
